import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  findGcsByPaymentTypesRequested,
  reportFilterAppliedSelector,
  reportGcsPaymentTypesLoadingSelector,
  reportFilterStartBusinessDateSelector,
  reportFilterEndBusinessDateSelector,
  reportFilterStartAccountingDateSelector,
  reportFilterEndAccountingDateSelector,
  reportFilterStartOperationDateSelector,
  reportFilterEndOperationDateSelector,
  reportFilterCountryCodeSelector,
} from '../state';

import {
  CountryFilter,
  HourRangeFilter,
  OperationTypeFilter,
  PaymentTypesFilter,
  PosFilter,
  ReportActionsGridItem,
  ReportFilterActions,
  ReportFilters,
  SegmentsFilter,
  StoreFilter,
  TransactionTypeFilter,
} from './ReportFilters';

import { AppPermissions } from '../types';
import {
  AccountingDateRangeFilter,
  BusinessDateRangeFilter,
  OperationDateRangeFilter,
} from './GcsFilters';
import { PermissionsProvider } from './Permissions';
import { DialogFiltersProps } from './ReportFilters/ReportFiltersProvider';
import { constants } from '../helpers';
import { FilterPanel, StatusChip } from './Visual';

const dialogFiltersConfig: DialogFiltersProps = {
  options: ['startOperationDate', 'endOperationDate', 'startAccountingDate', 'endAccountingDate'],
  selector: reportFilterAppliedSelector,
  filters: (
    <>
      <OperationDateRangeFilter maxDaysBetween={constants.reportsFilterLimitDateRangeThirtyOne} />
      <AccountingDateRangeFilter maxDaysBetween={constants.reportsFilterLimitDateRangeThirtyOne} />
    </>
  ),
};

const onedriveAction = () => findGcsByPaymentTypesRequested(true);

const GcsPaymentTypesSearch = () => {
  const { t } = useTranslation();
  const startBusinessDate = useSelector(reportFilterStartBusinessDateSelector);
  const endBusinessDate = useSelector(reportFilterEndBusinessDateSelector);
  const startAccountingDate = useSelector(reportFilterStartAccountingDateSelector);
  const endAccountingDate = useSelector(reportFilterEndAccountingDateSelector);
  const startOperationDate = useSelector(reportFilterStartOperationDateSelector);
  const endOperationDate = useSelector(reportFilterEndOperationDateSelector);
  const hasCountry = Boolean(useSelector(reportFilterCountryCodeSelector));
  const hasAnyDateRange =
    (startBusinessDate.accept && endBusinessDate.accept) ||
    (startAccountingDate.accept && endAccountingDate.accept) ||
    (startOperationDate.accept && endOperationDate.accept);
  const applyDisabled = !(hasAnyDateRange && hasCountry);
  const countryRequiredIncomplete = applyDisabled && !hasCountry;
  const dateRangeRequiredIncomplete = applyDisabled && !hasAnyDateRange;
  return (
    <ReportFilters
      dialogFiltersConfig={dialogFiltersConfig}
      loadingSelector={reportGcsPaymentTypesLoadingSelector}
      applyAction={findGcsByPaymentTypesRequested}
      onedriveAction={onedriveAction}
    >
      <PermissionsProvider resourcePermissions={[AppPermissions.GCS]}>
        <FilterPanel
          subtitleTranslationKey="gcs-payment-type.filters.subtitle"
          actions={
            <StatusChip
              status={applyDisabled ? 'warning' : 'success'}
              label={
                applyDisabled ? t('filters.status.missingRequired') : t('filters.status.ready')
              }
            />
          }
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3} lg={2} xl={2}>
              <CountryFilter fullWidth requiredIncomplete={countryRequiredIncomplete} />
            </Grid>
            <Grid item xs={12} md={3} lg={2} xl={2}>
              <StoreFilter />
            </Grid>
            <Grid item xs={12} md={3} lg={2} xl={2}>
              <PosFilter />
            </Grid>
            <Grid item xs={12} md={3} lg={2} xl={2}>
              <HourRangeFilter />
            </Grid>
            <Grid item xs={12} md={6} lg={4} xl={4}>
              <SegmentsFilter />
            </Grid>
            <Grid item xs={12} md={6} lg={4} xl={3}>
              <BusinessDateRangeFilter
                spacing={1}
                maxDaysBetween={constants.reportsFilterLimitDateRangeThirtyOne}
                requiredIncomplete={dateRangeRequiredIncomplete}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={2} xl={2}>
              <OperationTypeFilter />
            </Grid>
            <Grid item xs={12} md={4} lg={2} xl={2}>
              <TransactionTypeFilter />
            </Grid>
            <Grid item xs={12} md={4} lg={2} xl={2}>
              <PaymentTypesFilter labelTranslationId="table.columns.tender_name" />
            </Grid>
            <ReportActionsGridItem>
              <ReportFilterActions applyDisabled={applyDisabled} />
            </ReportActionsGridItem>
          </Grid>
        </FilterPanel>
      </PermissionsProvider>
    </ReportFilters>
  );
};

export default GcsPaymentTypesSearch;
